import React from 'react';
import { AlertTriangle, CheckCircle2, GitBranch, Info, ShieldCheck } from 'lucide-react';

import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { operatorText } from '../../lib/businessContracts';

const EMPTY_ARRAY = [];

/**
 * The business contract behind an action: why it is allowed, what it changes,
 * what blocks it, and how it is audited.
 *
 * This used to render as a four-cell wall of text inline on the page — on
 * screens that already carry a readout band, a checklist and a table, it was
 * a paragraph competing with the data. The content matters (it is the only
 * place that says *why* an action is blocked), so it is kept in full — but
 * behind an info affordance, consulted on demand.
 *
 * What stays visible is the one thing that changes behaviour: whether there
 * are blockers, and how many.
 *
 * The props are unchanged so the sixteen call sites keep working as they are.
 */
export default function BusinessActionContract({
  title = 'Contrat métier',
  contract,
  blockers = EMPTY_ARRAY,
  compact = false,
  tone,
}) {
  const [open, setOpen] = React.useState(false);
  // Le libellé ne passe jamais sur deux lignes: s'il ne tient pas dans la
  // place laissée entre l'icône et le badge, il est remplacé par l'icône et le
  // badge seuls (le texte reste lu par les lecteurs d'écran et en infobulle).
  const zoneRef = React.useRef(null);
  const mesureRef = React.useRef(null);
  const [tient, setTient] = React.useState(true);
  React.useLayoutEffect(() => {
    const zone = zoneRef.current;
    const mesure = mesureRef.current;
    if (!zone || !mesure) return undefined;
    const calculer = () => {
      const place = zone.clientWidth;
      // Sans mise en page (aucune largeur mesurable), on garde le texte.
      setTient(place === 0 && mesure.offsetWidth === 0 ? true : mesure.offsetWidth <= place);
    };
    calculer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(calculer);
    observer.observe(zone);
    return () => observer.disconnect();
  }, [title, blockers.length, contract]);
  if (!contract) return null;

  const hasBlockers =
    blockers.length > 0 ||
    String(contract.failure_message || '').includes('bloqué');

  const sections = [
    {
      icon: CheckCircle2,
      title: 'Pourquoi cette action est possible',
      items: contract.preconditions || [],
    },
    {
      icon: GitBranch,
      title: 'Ce que ça change',
      items: [contract.expected_effect, ...(contract.linked_effects || [])].filter(Boolean),
    },
    {
      icon: AlertTriangle,
      title: 'Ce qui peut bloquer',
      items: blockers.length ? blockers : [contract.failure_message].filter(Boolean),
      alert: true,
    },
    {
      icon: ShieldCheck,
      title: 'Anti-doublon et traçabilité',
      items: [contract.idempotency, contract.audit, contract.next_step].filter(Boolean),
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title={tient ? undefined : title}
        className={cn(
          'inline-flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
          hasBlockers
            ? 'border-flame/30 bg-flame/10 hover:bg-flame/15'
            : 'border-border bg-muted/50 hover:bg-muted',
          compact && 'py-1.5',
        )}
      >
        <span ref={zoneRef} className="relative flex min-w-0 flex-1 items-center gap-2">
          <Info
            className={cn(
              'h-4 w-4 shrink-0',
              hasBlockers ? 'text-flame-ink' : 'text-muted-foreground',
            )}
            aria-hidden="true"
          />
          <span
            className={tient ? 'whitespace-nowrap text-sm font-medium text-foreground' : 'sr-only'}
            data-testid="contrat-action-texte"
          >
            {title}
          </span>
          <span
            ref={mesureRef}
            aria-hidden="true"
            className="pointer-events-none invisible absolute left-6 top-0 whitespace-nowrap text-sm font-medium"
          >
            {title}
          </span>
        </span>
        <Badge
          variant={hasBlockers ? 'warning' : 'muted'}
          className="shrink-0 font-sans"
        >
          {blockers.length
            ? `${blockers.length} blocage${blockers.length > 1 ? 's' : ''}`
            : 'Contrôlé'}
        </Badge>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {sections.map((section) => (
              <ContractSection key={section.title} {...section} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContractSection({ icon: Icon, title, items, alert = false }) {
  const list = items.length ? items : ['Rien à signaler'];
  return (
    <section>
      <h3
        className={cn(
          'flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em]',
          alert && items.length ? 'text-flame-ink' : 'text-muted-foreground',
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {title}
      </h3>
      <ul className="mt-2 space-y-1.5 text-sm leading-6 text-foreground">
        {list.map((item) => (
          <li key={item} className="flex gap-2">
            <span
              className={cn(
                'mt-2.5 h-1 w-1 shrink-0 rounded-full',
                alert && items.length ? 'bg-flame' : 'bg-muted-foreground/50',
              )}
              aria-hidden="true"
            />
            <span>{operatorText(item)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
